/**
 * Record a shot-counter reading.
 *
 * The reading is logged, not overwritten - the cached number on the mould is
 * for lists, the log is the record. A count somebody can type over is a count
 * nobody can defend to a customer who is asking why their tool needed a rebuild
 * at 400,000 shots.
 *
 * The server refuses a reading below the last one. That is not pedantry: a
 * counter counts up, so a lower number is a typo, a reset, or a different
 * mould, and silently accepting it corrupts every "shots since service" figure
 * that follows.
 */
import { useEffect, useState } from 'react';
import { ApiClientError } from '@mouldtrack/shared';
import type { ShotReadingRow } from '@mouldtrack/shared';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input, Textarea } from '../../components/ui/field.js';
import { Code } from '../../components/ui/primitives.js';
import { createShotReading, isNetworkError, listShotReadings } from '../../api/client.js';
import type { MouldRow } from '../../db/queries.js';

export function ShotReadingDialog({
  mould,
  open,
  onOpenChange,
}: {
  mould: MouldRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [reading, setReading] = useState('');
  const [note, setNote] = useState('');
  const [history, setHistory] = useState<ShotReadingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    void listShotReadings(`?mouldId=${encodeURIComponent(mould.id)}&limit=8`)
      .then(setHistory)
      .catch(() => setHistory([])); // history is a courtesy; its absence must not block recording
  }, [open, mould.id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const value = Number(reading);
    if (!reading.trim() || !Number.isFinite(value) || value < 0) {
      setError('Enter the number on the counter.');
      return;
    }
    setSaving(true);
    try {
      await createShotReading({
        mouldId: mould.id,
        reading: Math.round(value),
        source: 'manual',
        note: note.trim() || null,
      });
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : isNetworkError(err)
            ? 'No connection. Readings are recorded online.'
            : 'Could not record that reading.',
      );
    } finally {
      setSaving(false);
    }
  }

  const current = mould.shotCount ?? null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Record a shot reading"
      description={`${mould.assetCode}${mould.name && mould.name !== mould.assetCode ? ` - ${mould.name}` : ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="shot-form" loading={saving}>
            Record
          </Button>
        </>
      }
    >
      <form id="shot-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label="Counter reading"
          required
          hint={
            current != null
              ? `Last recorded: ${current.toLocaleString()}. A reading below that is refused.`
              : 'The first reading for this mould.'
          }
        >
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={reading}
            onChange={(e) => setReading(e.target.value)}
            placeholder={current != null ? String(current + 1000) : '0'}
            autoFocus
          />
        </Field>

        <Field label="Note" hint="Why it was read, or anything unusual about it.">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </Field>

        {history.length ? (
          <div className="rounded-xl border border-border">
            <p className="border-b border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground">
              Recent readings
            </p>
            <ul className="divide-y divide-border">
              {history.map((h) => (
                <li key={h.id} className="flex items-baseline justify-between px-3 py-1.5 text-sm">
                  <span className="tabular font-medium">{h.reading.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(h.readAt).toLocaleDateString()}
                    {h.enteredBy?.name ? ` · ${h.enteredBy.name}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <p className="text-xs text-subtle-foreground">
          Recorded against <Code className="text-xs">{mould.assetCode}</Code> and kept as a log, not
          a single number that can be typed over.
        </p>
      </form>
    </Dialog>
  );
}
